import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { User, MapPin, ShieldCheck, Bell, Building2, LayoutGrid, Store, LogOut } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import api from "@/lib/api";

export default function AccountLayout() {
  const { user, logout } = useStore();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api.get("/notifications?limit=1").then((r) => { if (!cancelled) setUnread(r.data.unread); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!user) return null;
  const nav = [
    [LayoutGrid, "Overview", "/account", true],
    [User, "Profile", "/account/profile"],
    ...(user.role === "business_buyer" ? [[Building2, "Business", "/account/business"]] : []),
    [MapPin, "Addresses", "/account/addresses"],
    [ShieldCheck, "Security", "/account/security"],
    [Bell, "Notifications", "/account/notifications"],
    ...(user.role === "vendor" ? [[Store, "Vendor Onboarding", "/vendor/onboarding"]] : []),
  ];
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="account-layout">
      <div className="rounded-2xl bg-secondary text-white p-5 sm:p-6 flex items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-white/15 grid place-items-center font-serif font-bold text-xl">{(user.name || "U")[0]}</div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-serif font-bold truncate" data-testid="account-name">{user.name}</h1>
          <p className="text-white/80 text-sm truncate">{user.email}</p>
        </div>
        <button onClick={logout} data-testid="account-logout" className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/25 px-4 py-2 text-sm font-medium hover:bg-white/10"><LogOut size={14} /> Logout</button>
      </div>
      <div className="mt-6 grid lg:grid-cols-[230px_1fr] gap-6">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto hide-scrollbar rounded-xl border border-border bg-card p-2 lg:sticky lg:top-40 self-start">
          {nav.map(([Icon, label, to, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`account-nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap ${isActive ? "bg-secondary text-white" : "text-stone-600 hover:bg-muted"}`}>
              <Icon size={16} /> {label}
              {label === "Notifications" && unread > 0 && <span className="ml-auto rounded-full bg-primary text-white text-[10px] px-1.5 py-0.5">{unread}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0"><Outlet /></div>
      </div>
      {!user.email_verified && (
        <p className="mt-6 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-4" data-testid="verify-banner">
          Your email is not verified yet. <Link to="/account/security" className="font-semibold underline">Verify now</Link> to secure your account.
        </p>
      )}
    </div>
  );
}
